<!--
  The PR header's preview chip: "▶ Preview ↗ <host>" opening the primary
  preview (logic/previews.ts orders them, ready ones first), saying so when
  it is deploying, stale or failed, and "+N" opening a menu of the others.
-->
<script lang="ts">
  import { previewStatusText, previewWhy, type Preview } from '../logic/previews';
  import { relTime } from '../logic/time';
  import Popover from './Popover.svelte';

  interface Props {
    previews: Preview[];
  }
  let { previews }: Props = $props();

  const primary = $derived(previews[0]);
  const rest = $derived(previews.slice(1));
  let more: HTMLButtonElement | null = $state(null);
  let open = $state(false);
  const uid = Math.random().toString(36).slice(2, 8);

  /** The chip's words after "Preview", by status. */
  const suffix = (p: Preview) => (p.status === 'ready' ? '' : p.status === 'pending' ? ' deploying…' : p.status === 'failed' ? ' failed' : ` (${p.status})`);

  /** What a preview is and how it stands, in full (tooltips and menu items), with when. */
  function describe(p: Preview): string {
    const when = relTime(p.at);
    return `${previewWhy(p)}${when ? ` (${when})` : ''}`;
  }

  function close(refocus: boolean) {
    open = false;
    if (refocus) more?.focus();
  }
</script>

{#if primary}
  <span class="preview-chip {primary.status}">
    {#if primary.url}
      <a class="pv-main" href={primary.url} target="_blank" rel="noopener noreferrer" title={describe(primary)}
        ><span aria-hidden="true">▶</span> Preview{suffix(primary)} <span aria-hidden="true">↗</span>{#if primary.host && primary.url !== primary.logUrl}<span class="pv-host">{primary.host}</span>{/if}<span class="sr-only">
          (opens in a new tab): {describe(primary)}</span
        ></a
      >
    {:else}
      <span class="pv-main" title={describe(primary)}><span aria-hidden="true">▶</span> Preview{suffix(primary)}<span class="sr-only">: {describe(primary)}</span></span>
    {/if}
    {#if rest.length}
      <button
        bind:this={more}
        type="button"
        class="pv-more"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? `pv-menu-${uid}` : undefined}
        aria-label="{rest.length} more preview{rest.length === 1 ? '' : 's'}"
        onclick={() => (open = !open)}>+{rest.length}</button
      >
    {/if}
  </span>
  {#if open && more}
    <Popover anchor={more} role="menu" label="Other previews" id="pv-menu-{uid}" onclose={close}>
      {#each rest as p (p.label + (p.url ?? '') + p.source)}
        {#if p.url}
          <a role="menuitem" tabindex="-1" class="pv-item" href={p.url} target="_blank" rel="noopener noreferrer" onclick={() => close(false)}>
            <span class="pv-dot {p.status}" aria-hidden="true"></span>
            <span class="pv-name"><b>{p.url === p.logUrl || !p.host ? p.label : p.host}</b> <span class="pv-status {p.status}">{previewStatusText(p)}</span></span>
            <span class="pv-why">{describe(p)}</span>
          </a>
        {:else}
          <span role="menuitem" tabindex="-1" aria-disabled="true" class="pv-item">
            <span class="pv-dot {p.status}" aria-hidden="true"></span>
            <span class="pv-name"><b>{p.label}</b> <span class="pv-status {p.status}">{previewStatusText(p)}</span></span>
            <span class="pv-why">{describe(p)}</span>
          </span>
        {/if}
      {/each}
    </Popover>
  {/if}
{/if}

<style>
  .preview-chip {
    /* Holds its .sr-only text, which would otherwise widen the page. */
    position: relative;
    display: inline-flex;
    align-items: stretch;
    max-width: 100%;
    min-width: 0;
    border: 1px solid var(--good);
    border-radius: 30px;
    font-size: 12px;
    line-height: 1.6;
    background: var(--goodbg);
    color: var(--good);
    overflow: hidden;
  }
  .preview-chip.stale,
  .preview-chip.unverified,
  .preview-chip.inactive {
    border-color: var(--line);
    background: var(--code);
    color: var(--muted);
  }
  .preview-chip.pending {
    border-color: var(--warn);
    background: var(--warnbg);
    color: var(--warn);
  }
  .preview-chip.failed {
    border-color: var(--bad);
    background: var(--badbg);
    color: var(--bad);
  }
  .pv-main {
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    min-width: 0;
    padding: 1px 10px;
    color: inherit;
    text-decoration: none;
    white-space: nowrap;
    font-weight: 600;
  }
  a.pv-main:hover {
    text-decoration: underline;
    text-underline-offset: 3px;
  }
  .pv-host {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 400;
    font-family: ui-monospace, SFMono-Regular, Consolas, monospace;
    font-size: 11px;
  }
  .pv-more {
    flex: none;
    border: 0;
    border-left: 1px solid currentColor;
    background: none;
    color: inherit;
    padding: 1px 9px;
    font: inherit;
    font-weight: 600;
  }
  .pv-more:hover,
  .pv-more[aria-expanded='true'] {
    background: color-mix(in srgb, currentColor 12%, transparent);
  }
  .pv-item {
    display: grid;
    grid-template-columns: 10px minmax(0, 1fr);
    gap: 2px 8px;
    align-items: baseline;
    padding: 6px 8px;
    border-radius: 6px;
    color: var(--fg);
    text-decoration: none;
    min-width: min(320px, calc(100vw - 64px));
  }
  .pv-item:hover,
  .pv-item:focus {
    background: var(--soft);
    outline: none;
  }
  .pv-item:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: -2px;
  }
  .pv-name {
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .pv-why {
    grid-column: 2;
    font-size: 12px;
    color: var(--muted);
  }
  .pv-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--good);
    align-self: center;
  }
  .pv-dot.stale,
  .pv-dot.unverified,
  .pv-dot.inactive {
    background: var(--muted);
  }
  .pv-dot.pending {
    background: var(--warn);
  }
  .pv-dot.failed {
    background: var(--bad);
  }
  .pv-status {
    font-size: 11px;
    color: var(--good);
  }
  .pv-status.stale,
  .pv-status.unverified,
  .pv-status.inactive {
    color: var(--muted);
  }
  .pv-status.pending {
    color: var(--warn);
  }
  .pv-status.failed {
    color: var(--bad);
  }
</style>
